import { ChartTypes } from 'containers/Report/Components/TypeBtn/TypeBtn';
import { getTranslation } from 'utils/translation';
import { ReportConfig } from '../ReportConfig';
import COMMON_CONFIG from '../common/common.config';

// 공통 베이스 인터페이스
export interface SidebarBaseItem {
  show: boolean;
  title?: string;
  showHeaderButton?: boolean;
  showFormat?: boolean;
  showRange?: boolean;
  showSeriesOption?: boolean;
  hidePosition?: boolean;
}

// dataLabelPosition처럼 특수 속성(mode)이 있는 경우를 위한 확장
export interface SidebarDataItem extends SidebarBaseItem {
  mode?: 'barDataLabel' | 'comboDataLabel' | 'lineDataLabel' | 'pieDataLabel' | 'slicerLabel';
}

interface LegendItem extends SidebarBaseItem {
  markerOption?: SidebarBaseItem;
}

const { FIELD_RESOURCE_TYPE } = COMMON_CONFIG;

const _STRUCTURE_SIDEBAR_CONFIG: Record<
  ChartTypes,
  {
    labelList: SidebarBaseItem;
    valueList: SidebarBaseItem;
    subValueList: SidebarBaseItem;
    legendPosition: SidebarBaseItem;
    dataLabelPosition: SidebarDataItem;
    slicerOption: SidebarBaseItem;
  }
> = {
  CLUSTERED_COLUMN: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.CLUSTERED_COLUMN.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.CLUSTERED_COLUMN.values;
      },
    },
    subValueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.CLUSTERED_COLUMN.subValues;
      },
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'barDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_COLUMN: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_COLUMN.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_COLUMN.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'barDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_COLUMN_100: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_COLUMN_100.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_COLUMN_100.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'barDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  COMBO_CHART: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.COMBO_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.COMBO_CHART.values;
      },
    },
    subValueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.COMBO_CHART.subValues;
      },
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'comboDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  CLUSTERED_BAR: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.CLUSTERED_BAR.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.CLUSTERED_BAR.values;
      },
    },
    subValueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.CLUSTERED_BAR.subValues;
      },
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'barDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_BAR: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_BAR.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_BAR.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'barDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_BAR_100: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_BAR_100.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_BAR_100.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'barDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  LINE: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.LINE.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.LINE.values;
      },
    },
    subValueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.LINE.subValues;
      },
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_LINE: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_LINE.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_LINE.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_LINE_100: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_LINE_100.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_LINE_100.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  AREA: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.AREA.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.AREA.values;
      },
    },
    subValueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.AREA.subValues;
      },
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_AREA: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_AREA.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_AREA.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  STACKED_AREA_100: {
    labelList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_AREA_100.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.STACKED_AREA_100.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      showSeriesOption: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  PIE_CHART: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.PIE_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.PIE_CHART.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'pieDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  DOUGHNUT_CHART: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.DOUGHNUT_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.DOUGHNUT_CHART.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'pieDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  ROSE_CHART: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.ROSE_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.ROSE_CHART.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'pieDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  SUNBURST: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.SUNBURST.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.SUNBURST.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'pieDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  RADAR_CHART: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.RADAR_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.RADAR_CHART.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  SCATTER: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.SCATTER.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: true,
      get title() {
        return FIELD_RESOURCE_TYPE.SCATTER.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'lineDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  TREEMAP: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.TREEMAP.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.TREEMAP.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: false,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'barDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  GAUGE: {
    labelList: {
      show: false,
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.GAUGE.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: false,
    },
    dataLabelPosition: {
      show: false,
    },
    slicerOption: {
      show: false,
    },
  },
  TABLE_CHART: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.TABLE_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.TABLE_CHART.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: false,
    },
    dataLabelPosition: {
      show: false,
    },
    slicerOption: {
      show: false,
    },
  },
  MATRIC_TABLE: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.MATRIC_TABLE.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.MATRIC_TABLE.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: false,
    },
    dataLabelPosition: {
      show: false,
    },
    slicerOption: {
      show: false,
    },
  },
  SUMMARY_CARD: {
    labelList: {
      show: false,
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.SUMMARY_CARD.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: false,
    },
    dataLabelPosition: {
      show: false,
    },
    slicerOption: {
      show: false,
    },
  },
  FUNNEL_CHART: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.FUNNEL_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.FUNNEL_CHART.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: true,
      showHeaderButton: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: true,
      mode: 'pieDataLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: false,
    },
  },
  GEO_CHART: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.GEO_CHART.labels;
      },
    },
    valueList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.GEO_CHART.values;
      },
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: false,
    },
    dataLabelPosition: {
      show: false,
    },
    slicerOption: {
      show: false,
    },
  },
  CONTEXT_SELECTOR: {
    labelList: {
      show: true,
      showHeaderButton: false,
      get title() {
        return FIELD_RESOURCE_TYPE.CONTEXT_SELECTOR.labels;
      },
    },
    valueList: {
      show: false,
    },
    subValueList: {
      show: false,
    },
    legendPosition: {
      show: false,
    },
    dataLabelPosition: {
      show: true,
      showHeaderButton: false,
      showSeriesOption: false,
      hidePosition: true,
      mode: 'slicerLabel',
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    slicerOption: {
      show: true,
      get title() {
        return getTranslation('resource', 'OPTION');
      },
    },
  },
};

/**
 * - 규칙
 * - 값이 percent인 경우 데이터 레이블의 포맷은 설정할 수 없음
 */
const _CHART_STYLE_SIDEBAR_CONFIG: Record<
  ChartTypes,
  {
    chart?: {
      title: string;
    };
    placeholder: SidebarBaseItem;
    layout: SidebarBaseItem;
    label: SidebarBaseItem;
    value: SidebarBaseItem;
    xAxis: SidebarBaseItem;
    yAxis: SidebarBaseItem;
    subYAxis: SidebarBaseItem;
    lineStyle: SidebarBaseItem;
    barStyle: SidebarBaseItem;
    scatterStyle: SidebarBaseItem;
    pieStyle: SidebarBaseItem;
    treemapStyle: SidebarBaseItem;
    radarStyle: SidebarBaseItem;
    tableStyle: SidebarBaseItem;
    summaryStyle: SidebarBaseItem;
    gridlinesStyle: SidebarBaseItem;
    selectorStyle: SidebarBaseItem;
    dataLabel: SidebarBaseItem;
    tooltip: SidebarBaseItem;
    legend: LegendItem;
  }
> = {
  CLUSTERED_COLUMN: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
      },
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: true,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_COLUMN: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: true,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_COLUMN_100: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: false,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: true,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  CLUSTERED_BAR: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    subYAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'X_AXIS')}`;
      },
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: true,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_BAR: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: true,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_BAR_100: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: false,
      showRange: true,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: true,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  LINE: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
      },
    },
    lineStyle: {
      show: true,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_LINE: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: true,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_LINE_100: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: false,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: true,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  AREA: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
      },
    },
    lineStyle: {
      show: true,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_AREA: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: true,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  STACKED_AREA_100: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: false,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: true,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  PIE_CHART: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: true,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: false,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  DOUGHNUT_CHART: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: true,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: false,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  ROSE_CHART: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: true,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: false,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  SUNBURST: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: true,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: false,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  GAUGE: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'VALUE');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: false,
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: false,
    },
  },
  TABLE_CHART: {
    placeholder: {
      show: false,
    },
    chart: {
      get title() {
        return getTranslation('resource', 'TABLE');
      },
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: true,
      get title() {
        return getTranslation('resource', 'TABLE');
      },
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'STYLES');
      },
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: false,
    },
    tooltip: {
      show: false,
    },
    legend: {
      show: false,
    },
  },
  MATRIC_TABLE: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: false,
    },
    tooltip: {
      show: false,
    },
    legend: {
      show: false,
    },
  },
  SUMMARY_CARD: {
    chart: {
      get title() {
        return getTranslation('resource', 'SUMMARY_CARD');
      },
    },
    placeholder: {
      show: false,
    },
    label: {
      show: true,
    },
    value: {
      show: true,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: true,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: false,
    },
    tooltip: {
      show: false,
    },
    legend: {
      show: false,
    },
  },
  FUNNEL_CHART: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'FUNNEL');
      },
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showSeriesOption: false,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  GEO_CHART: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: true,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: false,
    },
    tooltip: {
      show: false,
    },
    legend: {
      show: false,
    },
  },
  COMBO_CHART: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: false,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: true,
      showFormat: true,
      showRange: true,
      get title() {
        return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
      },
    },
    lineStyle: {
      show: true,
    },
    barStyle: {
      show: true,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  SCATTER: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: true,
      showFormat: true,
      showRange: false,
      get title() {
        return getTranslation('resource', 'X_AXIS');
      },
    },
    yAxis: {
      show: true,
      showFormat: true,
      showRange: false,
      get title() {
        return getTranslation('resource', 'Y_AXIS');
      },
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: true,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: true,
      get title() {
        return getTranslation('resource', 'GRIDLINES');
      },
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: false,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  TREEMAP: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: true,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      showFormat: true,
      showSeriesOption: false,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: false,
    },
  },
  RADAR_CHART: {
    placeholder: {
      show: false,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: true,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: false,
    },
    dataLabel: {
      show: true,
      get title() {
        return getTranslation('resource', 'DATA_LABEL');
      },
    },
    tooltip: {
      show: true,
      get title() {
        return getTranslation('resource', 'TOOLTIP');
      },
    },
    legend: {
      show: true,
      get title() {
        return getTranslation('resource', 'LEGEND');
      },
      markerOption: {
        show: true,
      },
    },
  },
  CONTEXT_SELECTOR: {
    placeholder: {
      show: true,
    },
    label: {
      show: false,
    },
    value: {
      show: false,
    },
    layout: {
      show: false,
    },
    xAxis: {
      show: false,
    },
    yAxis: {
      show: false,
    },
    subYAxis: {
      show: false,
    },
    lineStyle: {
      show: false,
    },
    barStyle: {
      show: false,
    },
    scatterStyle: {
      show: false,
    },
    pieStyle: {
      show: false,
    },
    treemapStyle: {
      show: false,
    },
    radarStyle: {
      show: false,
    },
    tableStyle: {
      show: false,
    },
    gridlinesStyle: {
      show: false,
    },
    summaryStyle: {
      show: false,
    },
    selectorStyle: {
      show: true,
    },
    dataLabel: {
      show: false,
    },
    tooltip: {
      show: false,
    },
    legend: {
      show: false,
    },
  },
};

export default {
  get STRUCTURE_SIDEBAR_CONFIG() {
    return _STRUCTURE_SIDEBAR_CONFIG;
  },
  get CHART_STYLE_SIDEBAR_CONFIG() {
    return _CHART_STYLE_SIDEBAR_CONFIG;
  },
};
